// Excel export for the Manufacturing Collaboration tab view.
//
// The "Download" button (upper-right of the tabs bar) exports a workbook that
// contains the expanded row's MAIN summary table plus the table data of the
// currently-selected tab. Each tab's columns/rows are rebuilt here from the SAME
// data sources the tab components render from (manufacturingTabData helpers, the
// CPP / MFG Status / Yield static datasets), so the export matches what is shown
// on screen and includes ALL rows (not just the current page).
//
// Filename: "<batchNumber>_<Tab Name>.xlsx" (e.g. 2388-OXB-A-1_In-Process_Tests.xlsx).

import XLSX from 'xlsx-js-style';
import {
  MATERIALS_DATA, INPROCESS_DATA, RELEASE_DATA, EQUIPMENT_DATA,
  DEVIATION_DATA, PLANNED_CC_DATA, CHANGE_CONTROL_DATA, PLANNING_DATA,
  mergeTabData, selectTabData, fmtDate, fmtDateTime, fmtDateTimeSec,
} from './manufacturingTabData';
import { CPP_DATA, resolveCppBatch } from './cppStaticData';
import { selectDataset as selectMfgDataset } from './MFGStatusTab';
import { selectYield } from './YieldTab';
import { deviationDocs, ccDoc, coaDoc, bprDoc } from './mfgDocLinks';
import moment from 'moment';

const DEMO_PO = '1150345';

// ── value coercion ───────────────────────────────────────────────────────────
const cell = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return '';
  return String(v);
};

const isDemoPO = (rowData) =>
  rowData?.poNumber === DEMO_PO || rowData?.scheduleAgreementNumber === DEMO_PO;

const isSponsor = () => {
  try { return localStorage.getItem('collabType') !== 'CMO'; } catch (e) { return true; }
};

// ── MAIN summary table (the expanded row header) ─────────────────────────────
export const getMainTableExport = (rowData = {}) => {
  const cmoMfg = rowData.cmoMfg
    || [rowData.cmoName || rowData.sponsorName, rowData.vendorPlant || rowData.mfgPlantName]
      .filter(Boolean).join(' - ')
    || rowData.cmoName || '-';
  const productIdDesc = [rowData.productId, rowData.productDescription].filter(Boolean).join(' / ') || '-';
  const batchStatus = rowData.batchStatus === 'Planned'
    ? 'Shipped' : (rowData.batchStatus || 'NA');

  const headers = [
    'CMO - MFG', 'Product ID / Product Description', 'PO Number',
    'CMO Work Order Number', 'Stage', 'CMO Batch Number', 'Operation',
    'PO Status', 'Batch Status',
  ];
  const row = [
    cmoMfg,
    productIdDesc,
    rowData.poNumber || '-',
    rowData.cmoWorkOrderNumber || rowData.cmoWorkOrder || 'CMO12345',
    rowData.stage || 'NA',
    rowData.batchNumber || '-',
    rowData.operation || 'All',
    rowData.poStatus || '-',
    batchStatus,
  ].map(cell);

  return { name: 'Main Table', headers, rows: [row] };
};

// ── Deviations / CC static demo text (mirrors the tab components) ─────────────
const DEMO_SPONSOR_DEV_IDS = ['', 'SP-QE-2026-0004', '', 'SP-QE-2026-0007'];
const FIRST_ROW_PREVENTIVE_ACTION = `The filter cloth fixing checklist for Agitated Nutsche Filter Dryer (ANFD) (reference No. F-PR-113) shall be revised to incorporate the following checkpoints:
a) Verification of the straightness of the fixing plates by placing the fixing plates on the ANFD bottom dish.
b) A note point shall be incorporated as "If any abnormality is observed and rectification is performed during filter cloth fixing verification, all checkpoints shall be repeated by using a new checklist after the rectification".`;

const STATIC_CC_TYPES = ['Stage', 'Stage', 'Stage', 'Planned', 'Planned'];
const STATIC_CMO_CC_IDS = ['CMO-CC-2026-020', 'CMO-CC-2026-021', 'CMO-CC-2026-022', 'CMO-PCC-2026-101', 'CMO-PCC-2026-102'];
const STATIC_SPONSOR_CC_IDS = ['', 'SCC-2026-002', '', 'SCC-2026-101', ''];

// "Reporting Date / Step" — date + step split (mirrors DeviationsTab).
const splitDateStep = (v) => {
  const s = v == null ? '' : String(v);
  const idx = s.indexOf(' / ');
  return idx === -1 ? { date: s, step: '' } : { date: s.slice(0, idx), step: s.slice(idx + 3) };
};
const resolveReportingDate = (apiVal, excelVal) => {
  const a = splitDateStep(apiVal);
  const e = splitDateStep(excelVal);
  return { date: fmtDateTime(a.date || e.date), step: a.step || e.step };
};

const getProcedureNumber = (row) => {
  const unit = row.unitProcedure || '';
  const op = row.operation || '';
  const phase = row.phase || '';
  if (unit && op && phase) return `${unit}.${op}.${phase}`;
  if (unit && op) return `${unit}.${op}`;
  return unit || row.procedureNumber || '';
};
const fmtCT = (v) => { const m = moment(v); return m.isValid() ? m.format('DD-MMM-YYYY HH:mm:ss').toUpperCase() : ''; };
const fmtShip = (v) => (v ? moment(v).format('DD-MMM-YYYY').toUpperCase() : '');

// ── per-tab table builders ───────────────────────────────────────────────────
// Each returns an array of tables: { name, headers, rows: string[][] }.
const BUILDERS = {
  'Planning': ({ batchNumber }) => {
    const planning = selectTabData(PLANNING_DATA, batchNumber) || {};
    const headerFields = planning.headerFields || [];
    const milestones = planning.milestones || [];
    const tables = [];
    if (headerFields.length) {
      tables.push({
        name: 'Planning Summary',
        headers: ['Field', 'Value', 'Timestamp'],
        rows: headerFields.map((f) => [cell(f.label), cell(fmtDate(f.value)), cell(fmtDateTimeSec(f.timestamp))]),
      });
    }
    tables.push({
      name: 'Milestones',
      headers: ['Activity', 'Target Date', 'Expected Date', 'Actual Date', 'Last Update Timestamp'],
      rows: milestones.map((r) => [
        cell(r.item), cell(fmtDate(r.targetDate)), cell(fmtDate(r.expectedDate)),
        cell(fmtDate(r.actualDate)), cell(fmtDateTimeSec(r.lastUpdate)),
      ]),
    });
    return tables;
  },

  'MFG Status': ({ rowData }) => {
    const dataset = selectMfgDataset(rowData) || { updates: [] };
    return [{
      name: 'MFG Status',
      headers: ['Event Timestamp', 'Unit Name', 'Phase / Step Number', 'Unit Procedure',
        'Operation', 'Phase', 'Category', 'Event', 'Detail'],
      rows: (dataset.updates || []).map((r) => [
        cell([r.date, r.time].filter(Boolean).join(' ')),
        cell(r.unitName),
        cell(r.procedureNumber),
        cell(r.unitProcedure),
        cell(r.operation),
        cell(r.phase),
        cell(r.category),
        cell(r.event),
        cell(r.detail),
      ]),
    }];
  },

  'Materials & Consumption': ({ rowData, batchNumber, tabData }) => {
    const rows = mergeTabData(tabData?.rows, MATERIALS_DATA, batchNumber);
    return [{
      name: 'Materials & Consumption',
      headers: ['Unit Name', 'Phase / Step Number', 'Unit Procedure', 'Operation', 'Phase',
        'Material Description', 'CMO Material ID', 'CMO Batch Number',
        'Target Quantity', 'Actual Quantity', 'UoM', 'Quantity Variance (%)'],
      rows: rows.map((r) => [
        cell(r.unitName), cell(r.procedureNumber), cell(r.unitProcedure), cell(r.operation), cell(r.phase),
        cell(r.materialDescription), cell(r.cmoMaterialCode),
        cell(r.cmoBatchNumber), cell(r.targetQuantity), cell(r.actualQuantity),
        cell(r.uom), cell(r.quantityVariance),
      ]),
    }];
  },

  'In-Process Tests': ({ batchNumber, tabData }) => {
    const rows = mergeTabData(tabData?.rows, INPROCESS_DATA, batchNumber);
    return [{
      name: 'In-Process Tests',
      headers: ['Phase / Step Number', 'Operation Description', 'Method ID', 'Sample Description',
        'QC Receipt Date', 'Sample ID', 'Specification Limit', 'Result Value', 'UOM', 'Result', 'Analytical Report'],
      rows: rows.map((r) => [
        cell(r.procedureNumber), cell(r.operationDescription), cell(r.methodId), cell(r.sampleDescription),
        cell(fmtDate(r.qcReceiptDate)), cell(r.sampleId), cell(r.specificationLimit), cell(r.resultValue),
        cell(r.uom), cell(r.result), cell(r.analyticalReportNumber),
      ]),
    }];
  },

  'Product Release Tests': ({ batchNumber, tabData }) => {
    const rows = mergeTabData(tabData?.rows, RELEASE_DATA, batchNumber);
    return [{
      name: 'Product Release Tests',
      headers: ['Method ID', 'Sample Description', 'QC Receipt Date', 'Sample ID', 'Specification Limit',
        'Result Value', 'UOM', 'Result', 'Analytical Report'],
      rows: rows.map((r) => [
        cell(r.methodId), cell(r.sampleDescription), cell(fmtDate(r.qcReceiptDate)), cell(r.sampleId),
        cell(r.specificationLimit), cell(r.resultValue), cell(r.uom), cell(r.result), cell(r.analyticalReportNumber),
      ]),
    }];
  },

  'Deviations': ({ rowData, batchNumber, tabData }) => {
    const rows = mergeTabData(tabData?.rows, DEVIATION_DATA, batchNumber);
    const excelRows = selectTabData(DEVIATION_DATA, batchNumber) || [];
    const demo = isDemoPO(rowData);
    return [{
      name: 'Deviations',
      headers: ['Deviation Number', 'Reporting Date / Step', 'Initial Deviation Description',
        'Deviation Assessment Report', 'Final Deviation Report', 'Corrective Action', 'CMO CA Status',
        'Preventive Action', 'CMO PA Status', 'Deviation Status', 'Deviation Category', 'Deviation Type',
        'Informed Sponsor', 'Sponsor Deviation Status', 'Sponsor Deviation ID'],
      rows: rows.map((row, idx) => {
        const reporting = resolveReportingDate(row.reportingDate, (excelRows[idx] || excelRows[0])?.reportingDate);
        const sponsorDevId = (demo && DEMO_SPONSOR_DEV_IDS[idx]) ? DEMO_SPONSOR_DEV_IDS[idx] : (row.sponsorDeviationId || '');
        const hasSponsorDev = !!sponsorDevId;
        const sponsorDevStatus = row.sponsorDeviationStatus || 'Closed';
        const preventive = (demo && idx === 0) ? FIRST_ROW_PREVENTIVE_ACTION : (row.preventiveAction?.text || '');
        const cmoPA = (demo && idx === 0) ? 'Closed' : (row.cmoPAStatus || row.preventiveAction?.paStatus || '');
        return [
          cell(row.deviationNumber),
          cell([reporting.date, reporting.step].filter(Boolean).join(' / ')),
          cell(row.initialDescription),
          cell(row.assessmentReport?.name || 'QE-2026-005.Deviation Report.pdf'),
          cell(row.finalReport?.name || 'QE-2026-005.Final Deviation Report.pdf'),
          cell(row.correctiveAction?.text),
          cell(row.cmoCAStatus || row.correctiveAction?.caStatus),
          cell(preventive),
          cell(cmoPA),
          cell(row.deviationStatus),
          cell(row.deviationCategory),
          cell(row.deviationType),
          cell(row.informedSponsor),
          cell(hasSponsorDev ? sponsorDevStatus : '-'),
          cell(hasSponsorDev ? sponsorDevId : ''),
        ];
      }),
    }];
  },

  'CC': ({ rowData, batchNumber, tabData }) => {
    const unplanned = selectTabData(CHANGE_CONTROL_DATA, batchNumber) || [];
    const planned = mergeTabData(tabData?.rows, PLANNED_CC_DATA, batchNumber);
    const rows = [...unplanned, ...planned];
    return [{
      name: 'Change Control',
      headers: ['CC Type', 'Initial Document Number', 'Final Document Number', 'Change From', 'Change To',
        'Reasons For Change', 'CC Closure Report', 'CMO CC Status', 'CMO CC ID', 'Sponsor CC Status', 'Sponsor CC ID'],
      rows: rows.map((row, idx) => {
        const cmoCCId = row.cmoCCNumber || STATIC_CMO_CC_IDS[idx] || '';
        const sponsorCCId = row.sponsorCCNumber || STATIC_SPONSOR_CC_IDS[idx] || '';
        const hasSponsorCC = !!sponsorCCId;
        const closureName = row.ccClosureReport?.name
          || (cmoCCId ? `${cmoCCId}.Change Control Closer report.pdf` : 'CMO-CC-2026-020.Change Control Closer report.pdf');
        return [
          cell(row.ccType || STATIC_CC_TYPES[idx] || 'Stage'),
          cell(row.initialDocNumber),
          cell(row.finalDocNumber),
          cell(row.changeFrom),
          cell(row.changeTo),
          cell(row.reasonsForChange),
          cell(closureName),
          cell(row.cmoCCStatus || 'Closed'),
          cell(cmoCCId),
          cell(hasSponsorCC ? (row.sponsorCCStatus || 'Closed') : '-'),
          cell(hasSponsorCC ? sponsorCCId : ''),
        ];
      }),
    }];
  },

  'Cycle Time': ({ tabData }) => {
    const rows = tabData?.rows || [];
    const rowLabel = (idx) => (idx === 0 ? 'Start Procedure Number' : (idx === rows.length - 1 ? 'End Procedure Number' : 'Procedure Number'));
    const dateOverride = ['20-JAN-2026 10:30:00', '22-JAN-2026 08:30:00'];
    return [{
      name: 'Cycle Time',
      headers: ['', 'Procedure Number', 'Procedure Name', 'Actual Start Time', 'Actual End Time', 'Planned Cycle Time', 'Actual Cycle Time'],
      rows: rows.map((r, idx) => [
        cell(rowLabel(idx)),
        cell(getProcedureNumber(r)),
        cell(r.procedureName),
        cell(dateOverride[idx] || fmtCT(r.actualStartDate || r.actualStartTime?.date)),
        cell(dateOverride[idx] || fmtCT(r.actualEndDate || r.actualEndTime?.date)),
        cell(r.plannedCycleTimeStr || r.plannedCycleTime),
        cell(r.actualCycleTimeStr || r.actualCycleTime),
      ]),
    }];
  },

  'Equipment': ({ batchNumber, tabData }) => {
    const rows = mergeTabData(tabData?.rows, EQUIPMENT_DATA, batchNumber);
    return [{
      name: 'Equipment',
      headers: ['Phase / Step Number', 'Equipment ID', 'Location', 'Equipment Type', 'Description',
        'Actual Start Date', 'Actual End Date', 'Reservation ID'],
      rows: rows.map((r) => [
        cell(r.procedureNumber), cell(r.equipmentId), cell(r.location), cell(r.equipmentType),
        cell(r.description), cell(fmtDate(r.actualStartDate)), cell(fmtDate(r.actualEndDate)), cell(r.reservationId),
      ]),
    }];
  },

  'Yield': ({ rowData }) => {
    const dataset = selectYield(rowData) || { summary: {}, rows: [] };
    const s = dataset.summary || {};
    const tables = [{
      name: 'Batch Yield Summary',
      headers: ['Field', 'Value'],
      rows: [
        ['Batch Number', cell(s.batchNumber)],
        ['Planned Product Quantity', cell(s.plannedQuantity)],
        ['Actual Product Quantity', cell(s.actualQuantity)],
        ['UOM', cell(s.uom)],
        ['Molar Yield', cell(s.molarYield)],
        ['Batch Manufacturing Date', cell(fmtDate(s.mfgDate))],
        ['Storage Condition', cell(s.storageCondition)],
        ['Storage Location', cell(s.storageLocation)],
        ['Expiry Date', cell(fmtDate(s.expiryDate))],
      ],
    }, {
      name: 'Yield Detail',
      headers: ['Category', 'Level / Item', 'Yield Stream / Description', 'Quantity', 'UOM',
        dataset.countLabel || 'Viral Vector Count (TU)', 'Basis'],
      rows: (dataset.rows || []).map((r) => [
        cell(r.category), cell(r.item), cell(r.stream), cell(r.quantity), cell(r.uom), cell(r.tu), cell(r.basis),
      ]),
    }];
    return tables;
  },

  'Shipment': ({ tabData }) => {
    const rows = tabData?.rows || [];
    const plan = ['22-JAN-2026', '02-FEB-2026'];
    const del = ['23-JAN-2026', '03-FEB-2026'];
    return [{
      name: 'Shipment',
      headers: ['Activity', 'Planned Shipment Date', 'Actual Shipment Date', 'Expected Delivery Date', 'Actual Delivery Date'],
      rows: rows.map((r, idx) => [
        cell(r.shipmentType || r.item),
        cell(plan[idx] || fmtShip(r.plannedShipmentDate)),
        cell(plan[idx] || fmtShip(r.actualShipmentDate)),
        cell(del[idx] || fmtShip(r.expectedDeliveryDate)),
        cell(del[idx] || fmtShip(r.actualDeliveryDate)),
      ]),
    }];
  },

  'CPPs': ({ batchNumber }) => {
    const cppBatch = resolveCppBatch(batchNumber);
    const data = cppBatch ? CPP_DATA[cppBatch] : null;
    const rows = data?.rows || [];
    return [{
      name: 'CPPs',
      headers: ['Unit Name', 'Phase / Step Number', 'Unit Procedure', 'Operation', 'Phase',
        'Parameter', 'Set Point', 'Range', 'Actual Value', 'UoM'],
      rows: rows.map((r) => [
        cell(r.unitName), cell(r.procedureNumber), cell(r.unitProcedure), cell(r.operation), cell(r.phase),
        cell(r.parameter),
        cell(r.setPoint ?? ''), cell(r.range ?? ''), cell(r.actualValue ?? ''), cell(r.uom),
      ]),
    }];
  },

  'Documents': ({ batchNumber }) => {
    const dateOnly = (ts) => (ts ? String(ts).split(/[ T]/)[0] : '');
    const sections = [];
    const deviations = selectTabData(DEVIATION_DATA, batchNumber) || [];
    const ccs = selectTabData(PLANNED_CC_DATA, batchNumber) || [];

    const devRows = deviations.map((d) => {
      const served = deviationDocs(batchNumber, d) || {};
      const docs = [];
      if (d.assessmentReport?.name || served.assessment) docs.push(d.assessmentReport?.name || 'Deviation Assessment Report.pdf');
      if (d.finalReport?.name || served.final) docs.push(d.finalReport?.name || 'Final Deviation Report.pdf');
      return { id: d.deviationNumber, docs, uploadDate: dateOnly(d.finalReport?.timestamp || d.assessmentReport?.timestamp) };
    }).filter((r) => r.id && r.docs.length);
    if (devRows.length) {
      sections.push({
        name: 'Documents - Deviations',
        headers: ['Deviation ID', 'Documents', 'Upload Date'],
        rows: devRows.map((r) => [cell(r.id), cell(r.docs.join('; ')), cell(r.uploadDate)]),
      });
    }

    const ccRows = ccs.map((c) => {
      const name = c.ccClosureReport?.name || `${c.cmoCCNumber || c.sponsorCCNumber}.Change_Control_Report.pdf`;
      return { id: c.cmoCCNumber || c.sponsorCCNumber, name, uploadDate: dateOnly(c.ccClosureReport?.timestamp) };
    }).filter((r) => r.id);
    if (ccRows.length) {
      sections.push({
        name: 'Documents - Change Controls',
        headers: ['CC ID', 'Documents', 'Upload Date'],
        rows: ccRows.map((r) => [cell(r.id), cell(r.name), cell(r.uploadDate)]),
      });
    }

    const batchDocs = [];
    if (coaDoc(batchNumber)) batchDocs.push(['Certificate of Analysis', cell(coaDoc(batchNumber)), '']);
    if (bprDoc(batchNumber)) batchDocs.push(['Batch Production Record', cell(bprDoc(batchNumber)), '']);
    if (batchDocs.length) {
      sections.push({ name: 'Documents - Batch Documents', headers: ['Document', 'File', 'Upload Date'], rows: batchDocs });
    }
    return sections;
  },
};

// Build the tab's export tables. Returns [] for tabs with no tabular data (e.g. Comments).
export const getTabTables = ({ tabName, rowData, tabData }) => {
  const builder = BUILDERS[tabName];
  if (builder) {
    try {
      return builder({ rowData, batchNumber: rowData?.batchNumber, tabData }) || [];
    } catch (e) {
      console.error(`[tabExcelExport] builder failed for tab "${tabName}"`, e);
    }
  }
  // Generic fallback: use the API-mapped columns/rows if available.
  if (tabData?.columns?.length && tabData?.rows?.length) {
    const cols = tabData.columns;
    const headers = cols.map((c) => c.label || c.headerName || c.field || c.id || '');
    const keys = cols.map((c) => c.field || c.id || c.key);
    return [{
      name: tabName,
      headers,
      rows: tabData.rows.map((r) => keys.map((k) => cell(r?.[k]))),
    }];
  }
  return [];
};

// ── workbook assembly ────────────────────────────────────────────────────────
const HEADER_STYLE = {
  font: { bold: true, color: { rgb: '333333' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'F9F9FC' } },
  alignment: { vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'E0E0E0' } },
    bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
    left: { style: 'thin', color: { rgb: 'E0E0E0' } },
    right: { style: 'thin', color: { rgb: 'E0E0E0' } },
  },
};
const TITLE_STYLE = { font: { bold: true, sz: 12, color: { rgb: '1A1A2E' } } };
const BODY_STYLE = {
  alignment: { vertical: 'top', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'EEEEEE' } },
    bottom: { style: 'thin', color: { rgb: 'EEEEEE' } },
    left: { style: 'thin', color: { rgb: 'EEEEEE' } },
    right: { style: 'thin', color: { rgb: 'EEEEEE' } },
  },
};

const sanitizeFilePart = (s) => String(s || '')
  .replace(/&/g, ' ')
  .replace(/[\\/:*?"<>|]/g, '')
  .trim()
  .replace(/\s+/g, '_')
  .replace(/_+/g, '_');

const sanitizeSheetName = (s) => String(s || 'Sheet')
  .replace(/[\\/:*?[\]]/g, ' ').trim().slice(0, 31) || 'Sheet';

// Assemble a single worksheet: main table block, then each tab table block,
// separated by blank rows. Header rows are styled.
const buildWorksheet = (mainTable, tabTables) => {
  const aoa = [];
  const styleMap = []; // { r, c, style }
  let maxCols = 0;

  const pushBlock = (block) => {
    const rTitle = aoa.length;
    aoa.push([block.name]);
    styleMap.push({ r: rTitle, c: 0, style: TITLE_STYLE });

    const rHeader = aoa.length;
    aoa.push([...block.headers]);
    block.headers.forEach((_, c) => styleMap.push({ r: rHeader, c, style: HEADER_STYLE }));
    maxCols = Math.max(maxCols, block.headers.length);

    block.rows.forEach((row) => {
      const r = aoa.length;
      aoa.push([...row]);
      row.forEach((_, c) => styleMap.push({ r, c, style: BODY_STYLE }));
      maxCols = Math.max(maxCols, row.length);
    });
    aoa.push([]); // spacer
  };

  pushBlock(mainTable);
  (tabTables || []).forEach(pushBlock);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  styleMap.forEach(({ r, c, style }) => {
    const ref = XLSX.utils.encode_cell({ r, c });
    if (ws[ref]) ws[ref].s = style;
  });

  // Column widths from content (capped).
  const widths = [];
  for (let c = 0; c < maxCols; c += 1) {
    let w = 10;
    aoa.forEach((row) => {
      const v = row[c];
      if (v != null) w = Math.max(w, Math.min(60, String(v).length + 2));
    });
    widths.push({ wch: w });
  }
  ws['!cols'] = widths;
  return ws;
};

export const buildTabWorkbookBlob = ({ tabName, rowData, tabData }) => {
  const mainTable = getMainTableExport(rowData);
  const tabTables = getTabTables({ tabName, rowData, tabData });
  const ws = buildWorksheet(mainTable, tabTables);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(tabName || 'Export'));
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

export const buildTabExportFilename = ({ batchNumber, tabName }) => {
  const b = sanitizeFilePart(batchNumber) || 'Batch';
  const t = sanitizeFilePart(tabName) || 'Tab';
  return `${b}_${t}.xlsx`;
};

// Public: build + trigger download. Returns the filename used.
export const downloadTabExcel = ({ tabName, rowData, tabData }) => {
  const blob = buildTabWorkbookBlob({ tabName, rowData, tabData });
  const filename = buildTabExportFilename({ batchNumber: rowData?.batchNumber, tabName });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return filename;
};
